import { AdjustmentAmountInput, RadioGroup } from '@library/components/inputs';
import type {
  ChangeAdjusmentParams,
  ChangeAdjustmentValidationErrorParams,
  CreateAdjustment,
  CreateAdjustmentFormValidationErrors
} from '@library/types/members/memberAdjustments';
import {
  Combobox,
  type ComboboxOption,
  type ComboboxOptions
} from '@library/components/selects';
import { useCallback, useMemo } from 'react';
import { FormActionButtons } from '@library/components/buttons';
import { classNames } from '@library/utils';
import { selectCreateAdjusmentMaxLimit } from '@bo-selectors/members/memberTransactionMaxLimits';
import { useSelector } from 'react-redux';

export interface CreateMemberAdjustmentProps {
  adjustment: CreateAdjustment;
  adjustmentFormValidationErrors: CreateAdjustmentFormValidationErrors;
  adjustmentReasonOptions: ComboboxOptions;
  adjustmentTypes: Array<string>;
  onUpdateAdjustmentErrors: ({
    key,
    error
  }: ChangeAdjustmentValidationErrorParams) => void;
  onChangeAdjustment: ({ key, value }: ChangeAdjusmentParams) => void;
  onCancelModal: () => void;
  onSaveAdjustment: () => void;
}

export const CreateMemberAdjustment = ({
  adjustment,
  adjustmentFormValidationErrors,
  adjustmentReasonOptions,
  adjustmentTypes,
  onUpdateAdjustmentErrors,
  onChangeAdjustment,
  onCancelModal,
  onSaveAdjustment
}: CreateMemberAdjustmentProps) => {
  const createAdjusmentMaxLimit = useSelector(selectCreateAdjusmentMaxLimit);
  const { adjustmentAmount, adjustmentType, adjustmentReasonId } = adjustment;

  const {
    adjustmentAmount: adjustmentAmountError,
    adjustmentType: adjustmentTypeError,
    adjustmentReasonId: adjustmentReasonCodeError
  } = adjustmentFormValidationErrors;

  const saveDisabled =
    !adjustmentAmount ||
    !Number(adjustmentAmount) ||
    !adjustmentType ||
    !adjustmentReasonId ||
    !!adjustmentFormValidationErrors.adjustmentAmount;

  const selectedAdjustmentReasonOption = useMemo(
    () =>
      adjustmentReasonOptions.find(
        (reasonOption) => reasonOption.value === adjustmentReasonId
      ),
    [adjustmentReasonOptions, adjustmentReasonId]
  );

  const handleChangeAdjustmentAmount = useCallback(
    ({ value, error }: { value: string; error: string }) => {
      onChangeAdjustment({
        key: 'adjustmentAmount',
        value
      });

      if (adjustmentAmountError !== error) {
        onUpdateAdjustmentErrors({
          key: 'adjustmentAmount',
          error
        });
      }
    },
    [adjustmentAmountError, onChangeAdjustment, onUpdateAdjustmentErrors]
  );

  const handleChangeAdjusmentType = useCallback(
    (value: string) => {
      onChangeAdjustment({ key: 'adjustmentType', value });

      const error = !value ? 'Adjustment type is required' : '';

      if (adjustmentTypeError !== error) {
        onUpdateAdjustmentErrors({
          key: 'adjustmentType',
          error
        });
      }
    },
    [adjustmentTypeError, onChangeAdjustment, onUpdateAdjustmentErrors]
  );

  const handleChangeAdjusmentReason = useCallback(
    (option: ComboboxOption | null) => {
      onChangeAdjustment({
        key: 'adjustmentReasonId',
        value: option?.value
      });

      const error = !option?.value ? 'Adjustment reason code is required' : '';

      if (adjustmentReasonCodeError !== error) {
        onUpdateAdjustmentErrors({
          key: 'adjustmentReasonId',
          error
        });
      }
    },
    [adjustmentReasonCodeError, onChangeAdjustment, onUpdateAdjustmentErrors]
  );

  const disableCreateAdjusment = createAdjusmentMaxLimit === 0;

  return (
    <div data-testid="create-adjustment">
      <div className="flex flex-col gap-8 p-6">
        {disableCreateAdjusment && (
          <p
            className="text-roses text-wrap"
            data-testid="transaction-max-zero-error-message"
          >
            Cannot create an adjusment since the transaction max limit is 0
          </p>
        )}
        <div className="flex items-center gap-2">
          <AdjustmentAmountInput
            disabled={disableCreateAdjusment}
            error={adjustmentAmountError}
            limitAmount={createAdjusmentMaxLimit}
            onAdjustmentAmountValueChange={handleChangeAdjustmentAmount}
            value={adjustmentAmount}
          />
        </div>
        <div data-testid="adjustment-reasons-selection">
          <p className="text-soClean-180 w-full rounded-md pb-2 text-sm leading-6">
            Adjustment Reason
          </p>
          <div
            className={classNames(
              'shrink-0',
              adjustmentReasonCodeError ? 'border-roses border' : 'border-0'
            )}
          >
            <Combobox
              disabled={disableCreateAdjusment}
              id="adjustmentReason"
              isMulti={false}
              value={selectedAdjustmentReasonOption || null}
              onChange={handleChangeAdjusmentReason}
              options={adjustmentReasonOptions}
              closeMenuOnSelect
              valueClassName="py-[11px]"
            />
          </div>
          {adjustmentReasonCodeError && (
            <p
              className="text-roses pt-2 text-sm"
              data-testid="create-adjustment-reason-error"
            >
              {adjustmentReasonCodeError}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between px-2">
          <RadioGroup
            dataTestId="adjustment-type"
            disabled={disableCreateAdjusment}
            id="adjustmentType"
            onValueChange={handleChangeAdjusmentType}
            radioOptions={adjustmentTypes}
            selectedOption={adjustmentType}
          />
        </div>
      </div>
      <div className="py-2">
        <FormActionButtons
          className="pr-6"
          primary={{ onClick: onSaveAdjustment, disabled: saveDisabled }}
          secondary={{ onClick: onCancelModal }}
        />
      </div>
    </div>
  );
};
